import { inject, Injectable } from '@angular/core';

import { Intercom, boot, update, shutdown, trackEvent } from '@intercom/messenger-js-sdk';
import { Observable, of, ReplaySubject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import {
  AnalyticsAdapter,
  AnalyticsIntegrations,
  AnalyticsProps,
  JSONValue,
} from '@core/analytics-types';
import type { AnalyticsAPIMethods, AnalyticsClientOptions } from '@core/analytics-types';
import { ENVIRONMENT } from '@core/common-legacy';

import type { IntercomEnvironment } from './types';

const INTERCOM_SEMANTIC_TRAITS: string[] = [
  'avatar',
  'companies',
  'company',
  'company_id',
  'created',
  'created_at',
  'custom_attributes',
  'email',
  'id',
  'monthly_spend',
  'name',
  'phone',
  'plan',
  'remote_created_at',
  'remove',
  'user_hash',
  'user_id',
  'widget',
];

@Injectable()
export class AnalyticsIntercomAdapter extends AnalyticsAdapter {
  #env = inject<IntercomEnvironment>(ENVIRONMENT);
  #appId: string | undefined;
  #booted = false;

  public readonly ready$ = new ReplaySubject<void>(1);

  public load(
    _key: string,
    _options?: AnalyticsClientOptions,
  ): Observable<AnalyticsAPIMethods | null> {
    this.#appId = this.#env.intercom?.appId;

    if (!this.#appId) {
      console.warn('Intercom app ID not provided');
      return of(null).pipe(shareReplay(1));
    }

    try {
      Intercom({ app_id: this.#appId });
    } catch (err) {
      console.warn('Failed to initialize Intercom SDK', err);
      return of(null).pipe(shareReplay(1));
    }

    const client: AnalyticsAPIMethods = {
      track: this.track.bind(this),
      identify: this.identify.bind(this),
      page: () => null,
      alias: () => null,
      reset: this.reset.bind(this),
    };

    return of(client).pipe(shareReplay(1));
  }

  private track(eventName: string, eventProps?: AnalyticsProps, opts?: AnalyticsClientOptions) {
    if (!this.#booted || !this.shouldSendTo('Intercom', opts)) {
      return;
    }

    const intercomProps = eventProps ? this.formatIntercomTraits(eventProps) : undefined;
    trackEvent(eventName, intercomProps);
  }

  private identify(
    userId?: number,
    traits?: Record<string, string | number | boolean>,
    opts?: AnalyticsClientOptions,
    callback?: () => void,
  ) {
    if (!this.shouldSendTo('Intercom', opts)) {
      if (callback) {
        callback();
      }
      return;
    }

    const intercomOpts = this.getIntegrationOptions('Intercom', opts);
    const allTraits: Record<string, JSONValue> = {
      ...(traits ?? {}),
      ...intercomOpts,
    };

    if (userId) {
      allTraits.user_id = `${userId}`;
    }
    if (traits?.tellent_user_guid) {
      allTraits.user_id = `${traits.tellent_user_guid}`;
    }
    if (traits?.email) {
      allTraits.email = traits.email;
    }

    const data = this.formatIntercomTraits(allTraits);

    if (!this.#booted) {
      boot({ app_id: this.#appId!, ...data });
      this.#booted = true;
      this.ready$.next();
    } else {
      update(data);
    }

    if (callback) callback();
  }

  private reset() {
    if (!this.#booted) return;

    shutdown();
    this.#booted = false;
  }

  private getIntegrationOptions(
    integration: AnalyticsIntegrations,
    opts?: AnalyticsClientOptions,
  ): Record<string, JSONValue> {
    const options = opts?.integrations?.[integration];
    return typeof options === 'object' && options !== null ? options : {};
  }

  private formatIntercomTraits(
    traits: AnalyticsProps | Record<string, JSONValue>,
  ): Record<string, JSONValue> {
    return Object.entries(traits).reduce(
      (acc, [key, value]) => {
        const isSemantic = INTERCOM_SEMANTIC_TRAITS.includes(key);
        const isObject = typeof value === 'object' && value !== null && !Array.isArray(value);
        const isArray = Array.isArray(value);

        if (isSemantic || (!isObject && !isArray)) {
          acc[key] = value ?? null;
        }

        return acc;
      },
      <Record<string, JSONValue>>{},
    );
  }
}
